"use client";

import { CartItem } from "./types";
import { CheckoutModalLayout } from "./CheckoutModalLayout";
import { cardSurchargePercentage } from "./const";
import { Dispatch, SetStateAction } from "react";

import Link from "next/link";
import { ShowInfo } from "@punchup/data/legacy";

interface Props {
  show: ShowInfo;
  cart: CartItem[];
  comedianNames: string[];
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

const countries = ["United States", "Argentina", "Australia", "Canada"];

export const PaymentAndBilling = ({
  show,
  cart,
  comedianNames,
  activeStep,
  setActiveStep,
}: Props) => {
  const ticketCount = cart.reduce((sum, t) => sum + t.quantity, 0);

  const onContinueClick = () => {
    // TODO: validate the billing information
    // increment the step index
    setActiveStep((prev) => prev + 1);
  };

  return (
    <CheckoutModalLayout
      show={show}
      cart={cart}
      comedianNames={comedianNames}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      title="Payment & Billing"
      subtitle="Punchup checkout is always secure."
      buttonLabel="Confirm"
      buttonDisabled={false}
      onButtonClick={onContinueClick}
    >
      <div className="flex-1 flex flex-col gap-6">
        <AddPromoCode />
        <div className="flex flex-col gap-2 md:gap-4">
          <h2 className="text-primary-blue text-[16px] font-bold font-sans normal-case">
            Payment details
          </h2>
          <div className="flex flex-col bg-[#F7F7F7] px-4 py-3 md:px-8 md:py-6 rounded-lg gap-4">
            <div className="flex flex-col">
              <label
                className="text-primary-blue text-xs font-semibold mb-0.5"
                htmlFor="card-number"
              >
                Card number
              </label>
              <input
                className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                id="card-number"
                type="text"
                placeholder="1234 1234 1234 1234"
              />
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex flex-col flex-1">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="expiration-date"
                >
                  Expiration date
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="expiration-date"
                  type="text"
                  placeholder="MM/YY"
                />
              </div>
              <div className="flex flex-col flex-1">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="cvv"
                >
                  Security code
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="cvv"
                  type="text"
                  placeholder="123"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <label
                className="text-primary-blue text-xs font-semibold mb-0.5"
                htmlFor="country"
              >
                Country
              </label>
              <select
                className="flex-1 border border-[#C7C8C9] rounded-lg px-3 py-3 text-[14px]"
                id="country"
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-secondary-blue text-[12px] font-light font-sans normal-case">
                Amex, Discover, Mastercard and Visa are accepted. Final item
                price includes a surcharge of {cardSurchargePercentage * 100}%
                to cover credit card service fees.
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 md:gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-primary-blue text-[16px] font-bold font-sans normal-case">
              Delivery details
            </h2>
            <p className="text-secondary-blue text-[12px] font-light font-sans normal-case">
              We&apos;ll send your tickets to the email address you provide
              below.
            </p>
          </div>
          <div className="flex flex-col bg-[#F7F7F7] px-4 py-3 md:px-8 md:py-6 rounded-lg gap-4">
            <div className="flex flex-col">
              <label
                className="text-primary-blue text-xs font-semibold mb-0.5"
                htmlFor="name"
              >
                Full name
              </label>
              <input
                className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                id="name"
                type="text"
                placeholder="First name"
              />
            </div>

            <div className="flex flex-col">
              <label
                className="text-primary-blue text-xs font-semibold mb-0.5"
                htmlFor="email"
              >
                Email
              </label>
              <input
                className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                id="email"
                type="email"
                placeholder="Email"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 md:gap-4">
          <h2 className="text-primary-blue text-[16px] font-bold font-sans normal-case">
            Billing information
          </h2>
          <div className="flex flex-col bg-[#F7F7F7] px-4 py-3 md:px-8 md:py-6 rounded-lg gap-4">
            <div className="flex flex-col md:flex-row gap-2 w-full">
              <div className="flex flex-col flex-1">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="billing-first-name"
                >
                  First name
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="billing-first-name"
                  type="text"
                  placeholder="First name"
                />
              </div>
              <div className="flex flex-col flex-1">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="billing-last-name"
                >
                  Last name
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="billing-last-name"
                  type="text"
                  placeholder="First name"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <label
                className="text-primary-blue text-xs font-semibold mb-0.5"
                htmlFor="billing-address1"
              >
                Address Line 1
              </label>
              <input
                className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                id="billing-address1"
                type="text"
                placeholder="Street address, PO Box, Company name, etc."
              />
            </div>
            <div className="flex flex-col">
              <label
                className="text-primary-blue text-xs font-semibold mb-0.5"
                htmlFor="billing-address2"
              >
                Address Line 2{" "}
                <span className="text-secondary-blue">(optional)</span>
              </label>
              <input
                className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                id="billing-address2"
                type="text"
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="flex flex-col">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="billing-zip"
                >
                  ZIP Code
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="billing-zip"
                  type="text"
                  placeholder="ZIP/Postal Code"
                />
              </div>
              <div className="flex flex-col">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="country"
                >
                  Country
                </label>
                <select
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-3 py-3 text-[14px]"
                  id="country"
                >
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex flex-col flex-1">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="billing-city"
                >
                  City
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="billing-city"
                  type="text"
                  placeholder="City"
                />
              </div>
              <div className="flex flex-col flex-1">
                <label
                  className="text-primary-blue text-xs font-semibold mb-0.5"
                  htmlFor="billing-state"
                >
                  State/Province
                </label>
                <input
                  className="flex-1 border border-[#C7C8C9] rounded-lg px-4 py-3 text-[14px]"
                  id="billing-state"
                  type="text"
                  placeholder="State/Province"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row items-start gap-3 text-secondary-blue text-sm">
          <input type="checkbox" id="terms-agreement" className="mt-2" />
          <label htmlFor="terms-agreement">
            By purchasing {ticketCount > 1 ? "these tickets" : "this ticket"}, I
            agree to Punchup’s{" "}
            <Link
              href="/tos"
              className="text-primary underline underline-offset-2"
            >
              Terms & Conditions
            </Link>{" "}
            and authorize them to perform this transaction.
          </label>
        </div>
      </div>
    </CheckoutModalLayout>
  );
};

const AddPromoCode = () => {
  return (
    <div>
      <h3 className="text-primary-blue text-[14px] font-semibold font-sans normal-case">
        Have a Promo Code?
      </h3>
      <div className="flex flex-row w-full">
        <input
          type="text"
          placeholder="Enter your code"
          className="flex-1 border border-[#C7C8C9] rounded-l-lg border-r-0 px-4 py-3 text-[14px]"
        />
        <button
          type="submit"
          className="py-3.5 px-5 font-semibold text-white bg-primary-blue rounded-r-lg"
        >
          Apply
        </button>
      </div>
    </div>
  );
};
